/**
 * Эмбед: чужая страница в iframe.
 *
 * Отличается от остальных виджетов тем, что мы НЕ владеем
 * содержимым. Чат, алерты и счётчики целей приходят от провайдера,
 * исполняются под его origin и под его CSP. Наше здесь — рамка
 * и права, которые мы этой рамке выдаём.
 *
 * ПОЭТОМУ ГЛАВНОЕ В ЭТОМ ФАЙЛЕ — НАБОР АТРИБУТОВ, а не разметка.
 * Он взят из docs/05 дословно и закреплён тестами с ОБЕИХ сторон:
 * что нужное есть и что лишнего нет. Негативные проверки здесь
 * важнее позитивных — они не дадут «на минутку» добавить
 * allow-popups ради чужого виджета.
 */
import { asRecord } from './props.js';
const TYPE = 'embed';
/**
 * Права, выдаваемые фрейму. Ровно эти, и список закрыт.
 *
 * `allow-scripts` + `allow-same-origin` вместе безопасны ИМЕННО
 * здесь. Эта пара опасна, когда во фрейм грузится документ с НАШЕГО
 * origin: тогда он выходит из песочницы в наш контекст. Сторонний
 * виджет на чужом origin получает доступ только к своему хранилищу
 * и своим сокетам — что ему и нужно для работы.
 *
 * Порядок значений зафиксирован: атрибут сверяется тестом целиком,
 * а не по вхождению, иначе лишнее право прошло бы незамеченным.
 */
const SANDBOX = 'allow-scripts allow-same-origin';
/**
 * Чего фрейму НЕ выдаётся — и что случится, если выдать.
 *
 * Списком в коде, а не только в тесте: тот, кто соберётся дописать
 * сюда право, прочтёт причину раньше, чем допишет.
 *
 * - allow-top-navigation — чужой виджет уведёт страницу оверлея,
 *   и в эфире окажется чужой сайт вместо сцены;
 * - allow-popups — окно поверх эфира, которое стример не закроет:
 *   в Browser Source нет ни адресной строки, ни крестика;
 * - allow-modals — alert() останавливает поток и висит в кадре;
 * - allow-downloads — загрузка на машину стримера без его ведома;
 * - allow-forms — отправка данных со страницы, которую он видит
 *   как картинку.
 */
export const FORBIDDEN_SANDBOX = [
    'allow-top-navigation',
    'allow-top-navigation-by-user-activation',
    'allow-popups',
    'allow-popups-to-escape-sandbox',
    'allow-modals',
    'allow-downloads',
    'allow-forms',
    'allow-pointer-lock',
    'allow-presentation',
];
/**
 * Возможности платформы. `autoplay` обязателен: без него звуки
 * алертов молча не проиграются — браузер требует жеста
 * пользователя, а в Browser Source жестов не бывает вовсе.
 *
 * `encrypted-media` — для проигрывателей, которые иначе отказываются
 * стартовать даже на своём открытом содержимом.
 */
const ALLOW = 'autoplay; encrypted-media';
/**
 * Referrer не уходит НИКОГДА.
 *
 * Без этого браузер отправит провайдеру адрес нашего оверлея
 * вместе с overlay-токеном в заголовке Referer. То есть утечка
 * НАШЕГО секрета третьей стороне на каждом запросе фрейма
 * (docs/05).
 */
const REFERRER_POLICY = 'no-referrer';
/**
 * Сколько ждать события `load`, прежде чем считать эмбед отвалившимся.
 *
 * Чужая страница может не ответить вовсе: провайдер лёг, сеть у
 * стримера моргнула, адрес протух. Изнутри рамки мы этого не видим —
 * событие `error` для межсайтового фрейма браузер не даёт, и молчание
 * неотличимо от долгой загрузки. Единственный доступный признак —
 * время.
 *
 * Пятнадцать секунд, а не три: тяжёлый виджет с видео на медленной
 * сети грузится долго, и ложная тревога хуже молчания — она заставит
 * нажать «перезагрузить» на том, что и так вот-вот заработает.
 */
export const EMBED_LOAD_TIMEOUT_MS = 15_000;
/** Имя события, которым слой сообщает о себе наружу. */
export const EMBED_STATE_EVENT = 'owm:embed-state';
export const embedWidget = {
    type: TYPE,
    parse(raw) {
        const props = asRecord(TYPE, raw);
        return {
            sourceId: typeof props.sourceId === 'string' ? props.sourceId : '',
            // Адрес НЕ проходит через url() из props.ts, и это не упущение.
            // Тот разбор разрешает http и data; фрейму годится только
            // https — см. httpsOnly ниже. Адрес приходит не из сцены,
            // а из списка источников, и уже проверен сервером при
            // добавлении: это последний рубеж, а не первый
            url: httpsOnly(props.url),
            name: typeof props.name === 'string' ? props.name : '',
        };
    },
    mount(props) {
        const el = document.createElement('div');
        el.dataset.widget = TYPE;
        // ОВЕРЛЕЙ НЕ ИНТЕРАКТИВЕН. Без этого фрейм ловил бы мышь
        // в предпросмотре панели: модератор не смог бы ни выделить
        // слой, ни сдвинуть его — курсор уходил бы внутрь чужой
        // страницы (docs/05)
        el.style.pointerEvents = 'none';
        el.style.overflow = 'hidden';
        const frame = document.createElement('iframe');
        // Атрибутами, а не свойствами: свойства отражаются в атрибуты
        // не во всех реализациях DOM, а проверять этот набор надо
        // именно в разметке — там его читает браузер
        frame.setAttribute('sandbox', SANDBOX);
        frame.setAttribute('allow', ALLOW);
        frame.setAttribute('referrerpolicy', REFERRER_POLICY);
        // Фрейма не касается мышь и не достаёт клавиатура: слой
        // не интерактивен, и попасть табом внутрь чужой страницы
        // из панели быть не должно
        frame.setAttribute('tabindex', '-1');
        frame.style.border = '0';
        frame.style.width = '100%';
        frame.style.height = '100%';
        frame.style.display = 'block';
        frame.style.pointerEvents = 'none';
        // Заглушка на случай, когда адреса нет: слой обязан быть виден
        // как слой, иначе модератор не поймёт, что он вообще есть
        const placeholder = document.createElement('div');
        placeholder.dataset.role = 'masked';
        placeholder.style.width = '100%';
        placeholder.style.height = '100%';
        placeholder.style.display = 'grid';
        placeholder.style.placeItems = 'center';
        placeholder.style.font = '14px system-ui, sans-serif';
        placeholder.style.color = '#c9c9c9';
        placeholder.style.background = '#1c1c1c';
        placeholder.style.outline = '1px dashed #4a4a4a';
        placeholder.style.outlineOffset = '-1px';
        el.append(frame, placeholder);
        // ─── Сторож загрузки ────────────────────────────────────────
        //
        // ОТВАЛИВШИЙСЯ ЭМБЕД ОСТАВЛЯЕТ ПУСТОЕ МЕСТО. Ни надписи, ни
        // рамки, ни крестика: всё это увидели бы зрители, а поломка —
        // не их дело. Наружу уходит только отметка в разметке
        // и событие, по которому оверлей расскажет панели.
        let timer;
        const announce = (state) => {
            el.dataset.state = state;
            el.dispatchEvent(new CustomEvent(EMBED_STATE_EVENT, {
                bubbles: true,
                detail: { state },
            }));
        };
        const stopWatching = () => {
            if (timer !== undefined) {
                clearTimeout(timer);
                timer = undefined;
            }
        };
        const watch = () => {
            stopWatching();
            // Отметка в разметке, но БЕЗ события: наружу уходят только
            // жалобы, а «грузится» жалобой не является. К тому же
            // на первой загрузке узел ещё не в сцене, и всплывать
            // событию оттуда некуда
            el.dataset.state = 'loading';
            timer = setTimeout(() => announce('error'), EMBED_LOAD_TIMEOUT_MS);
        };
        // load приходит и на успешную загрузку, и на страницу ошибки
        // самого провайдера: снаружи фрейма их не различить, и делать
        // вид, что различаем, значило бы врать в статусе
        const onLoad = () => {
            stopWatching();
            announce('ready');
        };
        frame.addEventListener('load', onLoad);
        apply(el, frame, placeholder, props, watch);
        return {
            el,
            update(next) {
                apply(el, frame, placeholder, next, watch);
            },
            destroy() {
                stopWatching();
                frame.removeEventListener('load', onLoad);
                // Адрес снимается ДО удаления узла.
                //
                // Удаление из DOM останавливает фрейм, но пустой src
                // разрывает соединения провайдера сразу и наверняка.
                // Это и есть «Заглушить всё» из docs/05: звук эмбеда идёт
                // мимо нашего мастер-гейна, и приглушить его из JS нельзя —
                // только выгрузив фрейм.
                frame.removeAttribute('src');
                el.remove();
            },
        };
    },
    // preload НАМЕРЕННО НЕТ.
    //
    // У картинки предзагрузка бесплатна: браузер кладёт файл в кеш
    // и на этом всё. Здесь «подготовить заранее» означало бы загрузить
    // чужую страницу целиком — с её скриптами, сокетами и звуком —
    // ради слоя, который стоит за кадром и, может быть, никогда
    // не понадобится. Это ровно та трата процессора стримера во время
    // эфира, от которой хост и не создаёт припаркованные слои.
};
/**
 * Переносит свойства в уже существующие узлы.
 *
 * АДРЕС ПРИСВАИВАЕТСЯ ТОЛЬКО ПРИ ИЗМЕНЕНИИ, и здесь это дороже,
 * чем у картинки. Запись того же src перезагружает фрейм: чат
 * теряет накопленную историю, а алерты переподключаются к провайдеру
 * — посреди эфира и без всякой причины.
 */
function apply(el, frame, placeholder, props, watch) {
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. Тот же случай,
       что в image.ts. */
    const masked = props.url === '';
    if (masked) {
        // Адреса нет: фрейм не должен остаться заряженным прежним
        if (frame.hasAttribute('src')) {
            frame.removeAttribute('src');
        }
        el.dataset.masked = 'true';
        // Незаряженной рамке нечего грузить, и сторожить её нечего:
        // «не загрузилось за пятнадцать секунд» о слое без адреса
        // сказало бы неправду
        delete el.dataset.state;
    }
    else {
        if (frame.getAttribute('src') !== props.url) {
            frame.setAttribute('src', props.url);
            // Сторож заводится только на НОВОМ адресе: тот же src фрейм
            // не перезагружает, и ждать от него второго load нечего
            watch();
        }
        delete el.dataset.masked;
    }
    frame.style.display = masked ? 'none' : 'block';
    placeholder.style.display = masked ? 'grid' : 'none';
    placeholder.textContent = props.name === '' ? 'Эмбед без адреса' : `${props.name}: адрес скрыт`;
    // title — для доступности и для отладки: во вкладке OBS иначе
    // не понять, какой из фреймов какой
    const title = props.name === '' ? 'Эмбед' : props.name;
    if (frame.getAttribute('title') !== title) {
        frame.setAttribute('title', title);
    }
    el.dataset.sourceId = props.sourceId;
    /* eslint-enable no-param-reassign */
}
/**
 * Пропускает только https.
 *
 * Всё остальное превращается в пустоту, то есть в заглушку, а не
 * в ошибку слоя: адрес приходит из списка источников, а не от
 * человека, и ронять из-за него сцену незачем. Но и подставлять
 * во фрейм `javascript:` нельзя — в оверлее это доступ к сокету
 * канала.
 *
 * Сервер это уже проверил при добавлении источника. Проверка здесь
 * — последний рубеж: между сервером и этой строкой лежит сеть
 * и полтора клиента.
 */
function httpsOnly(value) {
    if (typeof value !== 'string' || value === '') {
        return '';
    }
    try {
        return new URL(value).protocol === 'https:' ? value : '';
    }
    catch {
        return '';
    }
}
//# sourceMappingURL=embed.js.map